import { useMediaDeviceSelect } from "@livekit/components-react";
import { Settings, Video } from "lucide-react";
import { useEffect, useState } from "react";
import { useStore } from "zustand";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { uiStore } from "@/lib/stores/ui-store";
import { BackgroundSelector } from "./background-selector";

type MediaDeviceSelectProps = {
	kind: MediaDeviceKind;
	placeholder?: string;
};

const DEVICE_ID_PREVIEW_LENGTH = 8;

function MediaDeviceSelect({ kind, placeholder }: MediaDeviceSelectProps) {
	const { devices, activeDeviceId, setActiveMediaDevice } =
		useMediaDeviceSelect({ kind });
	const deviceSelections = useStore(uiStore, (state) => state.deviceSelections);
	const setDeviceSelection = useStore(
		uiStore,
		(state) => state.setDeviceSelection
	);

	useEffect(() => {
		const savedDeviceId = deviceSelections[kind];
		if (
			savedDeviceId &&
			devices.some((d) => d.deviceId === savedDeviceId) &&
			activeDeviceId !== savedDeviceId
		) {
			setActiveMediaDevice(savedDeviceId);
		}
	}, [devices, kind, deviceSelections, activeDeviceId, setActiveMediaDevice]);

	const activeDevice = devices.find((d) => d.deviceId === activeDeviceId);
	const filteredDevices = devices.filter((d) => d.deviceId !== "default");

	const handleDeviceChange = (deviceId: string) => {
		setActiveMediaDevice(deviceId);
		setDeviceSelection(kind, deviceId);
	};

	return (
		<Select onValueChange={handleDeviceChange} value={activeDeviceId || ""}>
			<SelectTrigger className="w-full">
				<SelectValue placeholder={placeholder || "Select device"}>
					<span className="truncate">
						{activeDevice?.label ||
							`Device ${activeDeviceId?.slice(0, DEVICE_ID_PREVIEW_LENGTH)}` ||
							"Default device"}
					</span>
				</SelectValue>
			</SelectTrigger>
			<SelectContent>
				{filteredDevices.map((device) => (
					<SelectItem key={device.deviceId} value={device.deviceId}>
						{device.label ||
							`Device ${device.deviceId.slice(0, DEVICE_ID_PREVIEW_LENGTH)}`}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

type SettingsTab = "audio" | "video";

type VoiceVideoSettingsProps = {
	initialTab?: SettingsTab;
};

export function VoiceVideoSettings({
	initialTab = "audio",
}: VoiceVideoSettingsProps) {
	const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);

	return (
		<div className="flex h-[450px] w-full md:w-[800px]">
			<div className="w-[200px] flex-shrink-0 space-y-1 border-r bg-muted/30 p-3 md:w-[240px]">
				<button
					className={`flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 font-medium text-sm transition-colors ${
						activeTab === "audio"
							? "bg-primary/10 text-primary"
							: "text-muted-foreground hover:bg-accent hover:text-foreground"
					}`}
					onClick={() => setActiveTab("audio")}
					type="button"
				>
					<Settings className="size-5" />
					Audio
				</button>
				<button
					className={`flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 font-medium text-sm transition-colors ${
						activeTab === "video"
							? "bg-primary/10 text-primary"
							: "text-muted-foreground hover:bg-accent hover:text-foreground"
					}`}
					onClick={() => setActiveTab("video")}
					type="button"
				>
					<Video className="size-5" />
					Video
				</button>
			</div>

			<div className="flex-1 overflow-y-auto p-6">
				{activeTab === "audio" && (
					<div className="space-y-6">
						<div className="space-y-3">
							<Label className="font-semibold text-base text-foreground">
								Microphone
							</Label>
							<MediaDeviceSelect
								kind="audioinput"
								placeholder="Select microphone"
							/>
						</div>
						<div className="space-y-3">
							<Label className="font-semibold text-base text-foreground">
								Speaker
							</Label>
							<MediaDeviceSelect
								kind="audiooutput"
								placeholder="Select speaker"
							/>
						</div>
					</div>
				)}

				{activeTab === "video" && (
					<div className="space-y-6">
						{/* Camera Section */}
						<div className="space-y-3">
							<Label className="font-semibold text-base text-foreground">
								Camera
							</Label>
							<MediaDeviceSelect
								kind="videoinput"
								placeholder="Select camera"
							/>
						</div>

						{/* Background Effects */}
						<div className="space-y-3">
							<Label className="font-semibold text-base text-foreground">
								Background effects
							</Label>
							<BackgroundSelector />
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
